import { useEffect, useState } from "react";
import { Loader2, Clock, Save, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import AdminGate from "@/components/AdminGate";
import PageHeader from "@/components/PageHeader";
import { useSeo } from "@/lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminHoursPage() {
  useSeo({ title: "Admin · Opening Hours | Cool Runnings Cape Town", noindex: true });
  return (
    <AdminGate>
      <HoursEditor />
    </AdminGate>
  );
}

function HoursEditor() {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetch(`${API}/hours`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setForm(d))
      .catch(() => toast.error("Could not load the opening hours"));
  }, []);

  const set = (key) => (e) => {
    setForm({ ...form, [key]: e.target.value });
    setSaved(false);
  };

  const save = async () => {
    if (!form) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/hours`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      const d = await res.json();
      setForm(d);
      setSaved(true);
      toast.success("Opening hours saved — the website now shows the new times");
    } catch {
      toast.error("Could not save — please try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        pageName="Opening Hours"
        eyebrow="Staff area"
        title="OPENING HOURS"
        goodgirlTitle
        image="/admin-header.jpg"
      />
      <section className="mx-auto max-w-3xl px-4 pb-24 pt-16 sm:px-6" data-testid="admin-hours-section">
        {!form ? (
          <p className="flex items-center justify-center gap-2 py-20 text-lg font-bold text-[#6b5a3a]" data-testid="admin-hours-loading">
            <Loader2 size={20} className="animate-spin" /> Loading…
          </p>
        ) : (
          <>
            <p className="text-xl font-extrabold text-[#241c0d]" data-testid="admin-hours-intro">
              Type the times below and press the big green button. That's all.
            </p>

            <div className="mt-8 space-y-6">
              <div className="card-dark p-8" data-testid="admin-hours-weekday-card">
                <h3 className="text-2xl font-extrabold text-[#241c0d]">Monday to Friday</h3>
                <div className="mt-5 grid gap-5 sm:grid-cols-2">
                  <TimeBox label="Opens at" value={form.weekday_open} onChange={set("weekday_open")} testid="admin-hours-weekday-open" />
                  <TimeBox label="Closes at" value={form.weekday_close} onChange={set("weekday_close")} testid="admin-hours-weekday-close" />
                </div>
              </div>

              <div className="card-dark p-8" data-testid="admin-hours-weekend-card">
                <h3 className="text-2xl font-extrabold text-[#241c0d]">Weekends, Public- &amp; School Holidays</h3>
                <div className="mt-5 grid gap-5 sm:grid-cols-2">
                  <TimeBox label="Opens at" value={form.weekend_open} onChange={set("weekend_open")} testid="admin-hours-weekend-open" />
                  <TimeBox label="Closes at" value={form.weekend_close} onChange={set("weekend_close")} testid="admin-hours-weekend-close" />
                </div>
              </div>
            </div>

            <button
              onClick={save}
              disabled={busy}
              className="mt-10 flex w-full items-center justify-center gap-3 rounded-2xl bg-[#16a34a] px-8 py-6 text-2xl font-black text-white shadow-[0_12px_30px_rgba(22,163,74,0.4)] transition-all hover:bg-[#15803d] active:scale-[0.99] disabled:opacity-60"
              data-testid="admin-hours-save-btn"
            >
              {busy ? <Loader2 size={26} className="animate-spin" /> : saved ? <CheckCircle2 size={26} /> : <Save size={26} />}
              {busy ? "Saving…" : saved ? "Saved" : "Save Opening Hours"}
            </button>

            <p className="mt-6 flex items-center justify-center gap-2 text-center text-base font-bold text-[#6b5a3a]" data-testid="admin-hours-preview">
              <Clock size={16} /> The website shows: Mon–Fri {form.weekday_open || "…"} – {form.weekday_close || "…"} · Weekends &amp; Holidays {form.weekend_open || "…"} – {form.weekend_close || "…"}
            </p>
            <p className="mt-2 text-center text-xs font-semibold text-[#8a7b5e]">
              Write times like 09h00 or 09:00. The park stays closed when it rains.
            </p>
          </>
        )}
      </section>
    </>
  );
}

function TimeBox({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="block text-lg font-extrabold text-[#5f5233]">{label}</span>
      <input
        value={value || ""}
        onChange={onChange}
        placeholder="09h00"
        className="mt-2 w-full rounded-2xl border-2 border-[#241c0d]/20 bg-white px-5 py-4 text-3xl font-black text-[#241c0d] focus:border-[#f8b818] focus:outline-none"
        data-testid={testid}
      />
      {value && !/^(\d{1,2})[h:](\d{2})$/.test(value.trim()) && (
        <span className="mt-1 block text-xs font-bold text-[#d63a2f]">Use a time like 09h00</span>
      )}
    </label>
  );
}